import React from "react";
import { resolveUploadUrl } from "../../utils/apiClient";
import { countLeaveDays, formatDays } from "../../utils/leaveDays";
import styles from "./ApprovalList.module.css";

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const balanceKey = (request) =>
  `${request.employee_id}:${request.time_off_type_id ?? request.leave_type_id}`;

/**
 * Shared list of pending time-off requests with Accept / Deny / Edit actions.
 * Purely presentational - parents own the data fetching and handlers.
 */
export default function ApprovalList({
  requests = [],
  onApprove,
  onDeny,
  onEdit,
  balanceByKey,
  emptyMessage = "No pending time off requests",
}) {
  if (!requests.length) {
    return <p className={styles.noRequests}>{emptyMessage}</p>;
  }

  return (
    <div className={styles.requestsList}>
      {requests.map((request) => {
        const days = countLeaveDays(
          request.start_date,
          request.end_date,
          request.start_half_day,
          request.end_half_day
        );
        const hasHalfDay = request.start_half_day || request.end_half_day;
        const remaining = balanceByKey ? balanceByKey[balanceKey(request)] : undefined;
        const hasRemaining = remaining !== undefined && remaining !== null;
        const lowBalance = hasRemaining && Number(remaining) < days;

        return (
          <div key={request.leave_request_id} className={styles.requestItem}>
            <div className={styles.requestInfo}>
              <div className={styles.requestHeader}>
                <div className={styles.requestHeaderLeft}>
                  {request.employee_avatar ? (
                    <img
                      src={resolveUploadUrl(request.employee_avatar)}
                      alt={request.employee_name}
                      className={styles.avatar}
                    />
                  ) : (
                    <div className={styles.avatarFallback}>{getInitials(request.employee_name)}</div>
                  )}
                  <strong>{request.employee_name}</strong>
                </div>
                <span className={styles.requestType}>{request.type_name}</span>
              </div>

              <div className={styles.requestDates}>
                {new Date(request.start_date).toLocaleDateString()} -{" "}
                {new Date(request.end_date).toLocaleDateString()}
              </div>

              <div className={styles.requestMeta}>
                <span>
                  <strong>{formatDays(days)}</strong> working day{days === 1 ? "" : "s"} requested
                  {hasHalfDay ? " (incl. half-day)" : ""}
                </span>
                {hasRemaining && (
                  <span className={lowBalance ? styles.balanceLow : undefined}>
                    <strong>{formatDays(remaining)}</strong> day{Number(remaining) === 1 ? "" : "s"} remaining
                    {lowBalance ? " (insufficient balance)" : ""}
                  </span>
                )}
              </div>

              {request.reason && <div className={styles.requestReason}>{request.reason}</div>}
            </div>

            <div className={styles.requestActions}>
              <button
                type="button"
                className={`${styles.btn} ${styles.deny}`}
                onClick={() => onDeny(request.leave_request_id)}
              >
                Deny
              </button>
              <button
                type="button"
                className={`${styles.btn} ${styles.accept}`}
                onClick={() => onApprove(request.leave_request_id)}
              >
                Accept
              </button>
              {onEdit && (
                <button
                  type="button"
                  className={`${styles.btn} ${styles.edit}`}
                  onClick={() => onEdit(request)}
                >
                  Edit
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
